import { cn } from '@/lib/utils';
import { AccordionItem, AccordionTrigger, AccordionContent, Accordion } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Loader2, Edit3, Trash2, Calendar, CheckCircle2, MoreVertical } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatPedidoCode } from "@/lib/formatters";
import { usePedidoDetail } from "@/hooks/usePedidoDetail";
import { StatusBadge } from "./shared/StatusBadge";
import { StatusBadgeInteractive } from "./shared/StatusBadgeInteractive";
import { ItemBadge } from "./shared/ItemBadge";
import { GestorCessionarioBadge } from "./shared/GestorCessionarioBadge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EditableField } from "./shared/EditableField";
import { useUpdateItemPedido } from "@/hooks/useUpdateItemPedido";
import { useHasAnyRole } from "@/hooks/useHasRole";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ItemDetalhado } from "./ItemDetalhado";

interface PedidoCardProps {
  pedidoId: string;
  variant?: 'compact' | 'full';
  showActions?: boolean;
  onView?: () => void;
  onEditGestor?: () => void;
  onEditCessionario?: () => void;
  onEditItem?: (item: any) => void;
  onDeleteItem?: (itemId: string) => void;
}

export function PedidoCard({ 
  pedidoId, 
  variant = 'compact', 
  showActions = true,
  onView,
  onEditGestor,
  onEditCessionario,
  onEditItem,
  onDeleteItem,
}: PedidoCardProps) {
  const { data: pedido, isLoading } = usePedidoDetail(pedidoId);
  const updateItemMutation = useUpdateItemPedido();
  const isGestor = useHasAnyRole(['master', 'diretor', 'gerente', 'coordenador', 'supervisor']);

  const handleUpdateItem = async (itemId: string, field: string, value: any) => {
    await updateItemMutation.mutateAsync({
      itemId,
      updates: { [field]: value }
    });
  };

  // Função para calcular percentual de preenchimento
  const calcularPercentualPreenchimento = (item: any, pedido: any): { percentual: number; cor: string; label: string } => {
    // Se tem data de cancelamento → 100% VERMELHO
    if (item.data_cancelamento) {
      return { percentual: 100, cor: 'bg-danger', label: '✗ Cancelado' };
    }
    
    // Se tem data de ativação (e não tem cancelamento) → 100% VERDE
    if (item.data_ativacao) {
      return { percentual: 100, cor: 'bg-success', label: '✓ Ativo' };
    }
    
    // SENÃO → Calcula progresso baseado em TODOS os campos obrigatórios (AZUL)
    const camposObrigatorios: string[] = [];
    const camposPreenchidos: string[] = [];
    
    // Helper para validar campo
    const validarCampo = (nome: string, valor: any) => {
      camposObrigatorios.push(nome);
      if (valor !== null && valor !== undefined && valor !== '') {
        camposPreenchidos.push(nome);
      }
    };
    
    // ✅ 1. VALIDAR GESTOR DO CLIENTE (fonte única - sempre obrigatório)
    validarCampo('gestor.nome', pedido?.gestor_nome);
    validarCampo('gestor.telefone', pedido?.gestor_telefone);
    validarCampo('gestor.whatsapp', pedido?.gestor_whatsapp);
    validarCampo('gestor.email', pedido?.gestor_email);
    validarCampo('gestor.rg', pedido?.gestor_rg);
    validarCampo('gestor.cpf', pedido?.gestor_cpf);
    
    // 2. VALIDAR CAMPOS DO ITEM baseado no tipo de produto
    const produtoNome = item.produto?.nome?.toUpperCase() || '';
    const isPN = produtoNome.startsWith('PN ');
    const isPP = produtoNome.startsWith('PP ');
    const isMP = produtoNome.startsWith('MP ');
    const isFIBRA = produtoNome.includes('FIBRA');
    
    // Verificar se é status "ACEITE REALIZADO" ou posterior (para validar contrato)
    const statusNome = item.status_nome?.toUpperCase() || '';
    const precisaContrato = statusNome.includes('ACEITE') || 
                           statusNome.includes('FATURAMENTO') || 
                           statusNome.includes('ATIVO');
    
    if (isPN || isPP || isMP) {
      // Campos obrigatórios para PN/PP/MP
      validarCampo('ddd', item.ddd);
      validarCampo('linha', item.linha);
      validarCampo('plano_nome', item.plano_nome);
      validarCampo('plano_valor', item.plano_valor);
      
      // ✅ Cessionário só é obrigatório para PN e PP (não para MP)
      if (isPN || isPP) {
        validarCampo('cessionario.nome', pedido?.cessionario_nome);
      }
      
      // PN precisa de operadora doadora
      if (isPN) {
        validarCampo('operadora_doadora', item.operadora_doadora);
      }
      
      // Se está em fase de aceite ou posterior, precisa de contrato
      if (precisaContrato) {
        validarCampo('contrato_anexo_id', item.contrato_anexo_id);
      }
    } else if (isFIBRA) {
      // Campos obrigatórios para FIBRA (endereço completo)
      const endereco = item.endereco_instalacao || {};
      validarCampo('endereco.cep', endereco.cep);
      validarCampo('endereco.logradouro', endereco.logradouro);
      validarCampo('endereco.numero', endereco.numero);
      validarCampo('endereco.bairro', endereco.bairro);
      validarCampo('endereco.cidade', endereco.cidade);
      validarCampo('endereco.uf', endereco.uf);
    } else {
      // ⚠️ FALLBACK: Outros produtos (TV, etc.) - validar campos mínimos
      // Gestor precisa ter CPF e RG
      validarCampo('gestor.cpf', pedido?.gestor_cpf);
      validarCampo('gestor.rg', pedido?.gestor_rg);
      validarCampo('valor_unit', item.valor_unit);
    }
    
    // 3. CALCULAR PERCENTUAL
    const total = camposObrigatorios.length;
    const preenchidos = camposPreenchidos.length;
    const percentual = total > 0 ? Math.round((preenchidos / total) * 100) : 0;
    
    // 4. DEFINIR LABEL
    const label = percentual === 100 
      ? '✓ Pronto para envio' 
      : `${percentual}% completo`;
    
    /* §8: era `bg-primary` (azul) — azul nao existe na paleta Prime. Estado
       intermediario e' NEUTRO; verde e vermelho ficam porque sao desfecho. */
    return { percentual, cor: 'bg-muted-foreground/40', label };
  };

  // O cartão representa a venda (pedido-mãe) e agrega seus filhos operacionais.
  const { data: itens = [] } = useQuery({
    queryKey: ['pedido-card-itens-familia', pedidoId],
    queryFn: async () => {
      const { data: filhos } = await supabase
        .from('pedidos')
        .select('id')
        .eq('pedido_mae_id', pedidoId)
        .eq('deletado', false);
      const pedidoIds = [pedidoId, ...(filhos?.map((f: any) => f.id) || [])];

      // @ts-ignore - Nested selects profundos causam erro de inferência do TypeScript
      const result = await supabase
        .from('itens_pedido')
        .select(`
          id,
          seq,
          familia,
          quantidade,
          subtotal,
          faturamento_tipo,
          ddd,
          linha,
          operadora_doadora,
          endereco_instalacao,
          pedido_id,
          produto_id,
          valor_unit,
          plano_nome,
          plano_valor,
          franquia_gb,
          numero_simulacao_operadora,
          numero_pedido_operadora,
          mes_trabalho,
          data_ativacao,
          data_cancelamento,
          agenda_tipo,
          agenda_data,
          agenda_observacoes,
          observacoes_item,
          status_id,
          produto:produto_id(id, nome),
          status:status_id(id, nome, cor),
          pedido:pedido_id(
            id,
            numero_pedido
          )
        `)
        .in('pedido_id', pedidoIds)
        .order('pedido_id', { ascending: true })
        .order('seq', { ascending: true });
      
      const data = result.data;

      return (data || []).map(item => ({
        ...item,
        // quadro_atual e coluna_atual já vêm do item, não do pedido pai
        status_nome: item.status?.nome,
        status_cor: item.status?.cor,
      }));
    },
    enabled: !!pedidoId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        Pedido não encontrado
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const familiaPrincipal = itens[0]?.familia?.toUpperCase() || 'SEM FAMÍLIA';
  const qtdItens = pedido.qtd_produtos || itens.length;
  
  const ultimaAtualizacao = pedido.data_referencia 
    ? formatDistanceToNow(new Date(pedido.data_referencia), {
        addSuffix: true,
        locale: ptBR
      })
    : null;

  if (variant === 'compact') {
    return (
      <AccordionItem value={pedidoId} className="border rounded-lg mb-2">
        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
          <div className="flex items-center justify-between w-full text-left gap-3">
            <span className="shrink-0 text-[11.5px] font-bold tabular-nums">
              {formatPedidoCode(pedido.numero_pedido)}
            </span>

            <span className="font-bold uppercase text-sm shrink-0">
              {familiaPrincipal}
            </span>

            <span className="text-xs text-muted-foreground shrink-0">
              ({qtdItens} {qtdItens === 1 ? 'item' : 'itens'})
            </span>

            <span className="font-semibold text-sm shrink-0">
              {formatCurrency(pedido.total_faturamento || 0)}
            </span>

            {/* Resumo de status dos itens */}
            <span className="text-xs text-muted-foreground shrink-0">
              {itens.length > 0 ? `${itens.length} ${itens.length === 1 ? 'item' : 'itens'}` : 'Sem itens'}
            </span>

            {ultimaAtualizacao && (
              <span className="text-xs text-muted-foreground shrink-0">
                {ultimaAtualizacao}
              </span>
            )}

            {showActions && onView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView();
                }}
                className="p-1.5 rounded hover:bg-muted shrink-0"
                title="Ver pedido completo"
              >
                <Eye className="h-4 w-4" />
              </button>
            )}
          </div>
        </AccordionTrigger>

        <AccordionContent className="px-4 pb-4 pt-2">
          <div className="space-y-3">
            <h4 className="font-semibold text-sm mb-3">Produtos do Pedido:</h4>
            
            {itens.map((item: any) => {
              const isPNorPP = item.produto?.nome?.match(/^P[NP]\s/i);

              return (
                <div key={item.id} className="border-l-2 border-border pl-4 py-2">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[11.5px] font-semibold tabular-nums text-foreground">
                      {item.numero_pedido_completo || `#P${item.pedido?.numero_pedido || 0}-${item.seq}`}
                    </span>
                    {item.pedido_id && item.quadro_atual && (
                      <StatusBadgeInteractive
                        pedidoId={item.pedido_id}
                        quadro={item.quadro_atual}
                        coluna={item.coluna_atual || ''}
                        statusNome={item.status_nome}
                        statusCor={item.status_cor}
                      />
                    )}
                    <span className="font-medium text-sm">
                      {item.produto?.nome || 'Produto sem nome'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <ItemBadge 
                      familia={item.familia} 
                      faturamentoTipo={item.faturamento_tipo}
                    />
                    <span className="font-semibold text-sm">
                      {formatCurrency(item.subtotal)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      • Qtd: {item.quantidade}
                    </span>
                  </div>

                  {/* Linha e Operadora (PN/PP) */}
                  {isPNorPP && (item.ddd || item.linha) && (
                    <div className="text-sm text-muted-foreground mt-1 space-y-1">
                      <div>
                         {item.ddd} {item.linha}
                        {item.operadora_doadora && ` • ${item.operadora_doadora}`}
                      </div>
                {/*  Gestor e Cessionário vêm do CLIENTE (pedido) */}
                {pedido?.gestor_nome && (
                  <div>
                     Gestor: <span className="font-medium">{pedido.gestor_nome}</span>
                    {pedido.gestor_telefone && <span className="text-xs ml-2"> {pedido.gestor_telefone}</span>}
                  </div>
                )}
                {pedido?.cessionario_nome && (
                  <div>
                     Cessionário: <span className="font-medium">{pedido.cessionario_nome}</span>
                  </div>
                )}
                      {(item.plano_nome || item.plano_valor || item.franquia_gb) && (
                        <div>
                           Plano: <span className="font-medium">{item.plano_nome || 'N/A'}</span>
                          {item.plano_valor && ` • R$ ${item.plano_valor.toFixed(2).replace('.', ',')}`}
                          {item.franquia_gb && ` • ${item.franquia_gb}GB`}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Endereço de Instalação (FIBRA) */}
                  {item.produto?.nome?.toUpperCase().includes('FIBRA') && item.endereco_instalacao && (
                    <div className="text-sm text-muted-foreground mt-1 space-y-1">
                      <div>
                         {item.endereco_instalacao.logradouro && `${item.endereco_instalacao.logradouro}, `}
                        {item.endereco_instalacao.numero}
                        {item.endereco_instalacao.complemento && ` - ${item.endereco_instalacao.complemento}`}
                        {item.endereco_instalacao.bairro && ` - ${item.endereco_instalacao.bairro}`}
                        {item.endereco_instalacao.cidade && ` - ${item.endereco_instalacao.cidade}`}
                        {item.endereco_instalacao.uf && `/${item.endereco_instalacao.uf.toUpperCase()}`}
                        {item.endereco_instalacao.cep && ` - CEP: ${item.endereco_instalacao.cep}`}
                      </div>
                      {(item.plano_nome || item.plano_valor) && (
                        <div>
                           Plano: <span className="font-medium">{item.plano_nome || 'N/A'}</span>
                          {item.plano_valor && ` • R$ ${item.plano_valor.toFixed(2).replace('.', ',')}`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  }

  // Variant 'full' - usado no PainelPedido
  return (
    <div className="space-y-4">
      {/* Resumo Financeiro */}
      {/* §7: Venda Nova vinha em verde e Migração em âmbar. São valores do MESMO
          tipo — a cor ali dizia que "R$ 0,00 de venda nova" é coisa boa e que
          migração é alerta. Uma cor só; o zero vai para muted (é ausência de
          valor, não valor); só o Total tem peso maior. */}
      <div className="grid grid-cols-3 gap-4 rounded-[10px] bg-accent px-3.5 py-[11px]">
        <div>
          <p className="text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">Venda Nova</p>
          <p className={cn(
            'font-display text-[16px] font-bold tabular-nums',
            pedido.total_venda_nova ? 'text-foreground' : 'text-muted-foreground',
          )}>
            {formatCurrency(pedido.total_venda_nova || 0)}
          </p>
        </div>
        <div>
          <p className="text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">Migração</p>
          <p className={cn(
            'font-display text-[16px] font-bold tabular-nums',
            pedido.total_migracao ? 'text-foreground' : 'text-muted-foreground',
          )}>
            {formatCurrency(pedido.total_migracao || 0)}
          </p>
        </div>
        <div>
          <p className="text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">Total</p>
          <p className="font-display text-[17px] font-bold tabular-nums text-foreground">
            {formatCurrency(pedido.total_faturamento || 0)}
          </p>
        </div>
      </div>
    </div>
  );
}
